/** @jsxImportSource react */
import * as Typeform from '@typeform/embed-react';
const { Sidetab } = Typeform;

import { getUUID } from '@common/helpers/cookie';
import { normJsonApi } from '@common/helpers/jsonApi';
import PopupButton from '@react/cta/TypeformWizard/TypeformPopupButton';
import TypeformWidget from '@react/cta/TypeformWizard/TypeformWidget';
import { useQuery } from '@react/services/api';
import { useAuth, useUser } from '@react/services/auth';
import { useEvents } from '@react/services/events';
import { usePopups } from '@react/services/popupsContext';
import { array, bool, func, object, string } from 'proptypes';
import React, { useCallback } from 'react';

const WizardTypeWrapper = ({
  wizardType,
  formId,
  onSubmit,
  buttonText,
  onReadyHandler,
  onQuestionChangedHandler,
  typeformStartEventArgs,
  onCloseHandler,
  ...props
}) => {
  const { user } = useUser();
  // const { setDisableChatBot } = useChatbox();
  // FIXME

  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : '';

  const { events } = useEvents();
  const { setPopupActive } = usePopups();

  const customOnReadyHandler = (wizardType) => {
    if (typeformStartEventArgs && typeformStartEventArgs.length) {
      if (typeformStartEventArgs[1]) {
        typeformStartEventArgs[1].formId = formId;
      }
      events.track(...typeformStartEventArgs);
    }
    if (wizardType && wizardType != 'WIDGET') {
      // setDisableChatBot(true);
    }
    if (onReadyHandler) {
      onReadyHandler();
    }
  };

  const onClose = () => {
    if (onCloseHandler) {
      onCloseHandler();
    }
    setPopupActive(false);
    // setDisableChatBot(false);
  };

  const { typeformHiddenFields } = props || {};

  const hiddenFields = {
    ...typeformHiddenFields,
    page: pathname,
    redirect_url: pathname?.[0] === '/' ? pathname.substring(1) : pathname,
  };
  hiddenFields.logged_in = !!user;

  if (typeformStartEventArgs?.length > 1) {
    const { cta, entry } = typeformStartEventArgs[1];
    hiddenFields.cta = cta;
    hiddenFields.entry = entry;
  }

  hiddenFields.uuid = getUUID();

  if (wizardType === 'POPUP_BUTTON') {
    return (
      <PopupButton
        id={formId}
        onSubmit={onSubmit}
        buttonText={buttonText}
        popupButtonClickHandler={() => customOnReadyHandler('POPUP_BUTTON')}
        onClose={onClose}
        hidden={hiddenFields}
        disabled={false}
        autoClose
        {...props}
      />
    );
  } else if (wizardType === 'WIDGET') {
    return (
      <TypeformWidget
        id={formId}
        onReady={() => customOnReadyHandler('WIDGET')}
        hidden={hiddenFields}
        onClose={onClose}
        onSubmit={onSubmit}
        onQuestionChanged={onQuestionChangedHandler}
        style={{ width: '100%', height: '100%' }}
      />
    );
  } else if (wizardType === 'SIDE_TAB') {
    return (
      <Sidetab
        id={formId}
        onReady={() => customOnReadyHandler('SIDE_TAB')}
        onClose={onClose}
        hidden={hiddenFields}
        onSubmit={onSubmit}
        onQuestionChanged={onQuestionChangedHandler}
      />
    );
  }
  return null;
};

WizardTypeWrapper.propTypes = {
  wizardType: string.isRequired,
  formId: string.isRequired,
  onSubmit: func.isRequired,
  buttonText: string,
  onReadyHandler: func,
  onQuestionChangedHandler: func,
  layout: string,
  sendEvent: func,
  typeformStartEventArgs: array,
};

const delay = (time) => new Promise((resolve) => setTimeout(resolve, time));

const TypeformWizard = (props) => {
  const { formId, onTypeformSubmissionHandler, typeformSubmitEventArgs } =
    props;
  const { events } = useEvents();

  const { createOrUpdateUser, user } = useAuth();
  const getTypeformDetails = useQuery('getTypeformResponseDetails');
  const getTypeformDetailsCall = (internalFormId, responseId) => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const typeformDetails = await getTypeformDetails({
          'filter[form]': internalFormId,
          'filter[response]': responseId,
        });
        resolve(typeformDetails);
      } catch (err) {
        reject(err);
      }
    });
  };

  const getTypeformDetailsWithRetry = async (
    retryLimit,
    internalFormId,
    responseId,
    retriesCount = 0
  ) => {
    return new Promise((resolve, reject) => {
      getTypeformDetailsCall(internalFormId, responseId).then(
        (res) => {
          resolve(res);
        },
        (err) => {
          console.log('err', err);
          if (retriesCount < retryLimit) {
            delay(500).then(async () => {
              const res = await getTypeformDetailsWithRetry(
                retryLimit,
                internalFormId,
                responseId,
                retriesCount + 1
              );
              resolve(res);
            });
          } else {
            reject(err);
          }
        }
      );
    });
  };

  const onSubmit = useCallback(
    async (e) => {
      if (typeformSubmitEventArgs && typeformSubmitEventArgs.length) {
        if (typeformSubmitEventArgs[1]) {
          typeformSubmitEventArgs[1].formId = formId;
        }
        events.track(...typeformSubmitEventArgs);
      }
      const { isConversionForm } = props;
      if (!user && isConversionForm) {
        const { response_id: responseId } = e;
        if (responseId) {
          try {
            const res = await getTypeformDetailsWithRetry(
              5,
              formId,
              responseId
            );
            const re = normJsonApi(res);
            const email = re.email;
            const name = re.name;
            const phone = re.phoneNumber;
            await createOrUpdateUser({
              name,
              phone,
              email,
            });
          } catch (err) {
            console.log(err);
          }
        }
      }

      if (onTypeformSubmissionHandler) {
        onTypeformSubmissionHandler();
      }
    },
    [user]
  );

  return (
    <>
      <div
        className='h-full'
        data-tp-id='gen-div-4774e07f-0e07-4e43-be09-d38aa8ac98a4'
      >
        <WizardTypeWrapper onSubmit={onSubmit} {...props} />
      </div>
    </>
  );
};

TypeformWizard.defaultProps = {
  isConversionForm: true,
};

TypeformWizard.propTypes = {
  formId: string,
  typeformStartEventArgs: array,
  typeformHiddenFields: object,
  onTypeformSubmissionHandler: func,
  isConversionForm: bool,
};

export default TypeformWizard;
